// STRICT VERSION FOR TESTING - Makes tests fail more easily
// Copy of usePreflightTest with much stricter thresholds

import { useState, useRef, useCallback } from 'react';
import {
  Room,
  RoomEvent,
  ConnectionState,
  DataPacket_Kind,
  RoomOptions,
  LocalParticipant
} from 'livekit-client';
import * as Sentry from '@sentry/react';

export interface NetworkMetrics {
  bandwidth: number; // Mbps
  latency: number; // ms
  packetLoss: number; // percentage
  jitter: number; // ms
  score: number; // 0-100
}

export interface HardwareMetrics {
  cpu: 'low' | 'medium' | 'high';
  memory: number; // GB
  browser: string;
  webrtcSupport: boolean;
  score: number; // 0-100
}

export interface DeviceAvailability {
  camera: boolean;
  microphone: boolean;
  speaker: boolean;
}

export interface DataChannelTestResult {
  reliable: boolean;
  lossy: boolean;
  bidirectional: boolean;
  latency: number;
}

export interface PreflightTestResult {
  overall: 'pass' | 'warning' | 'fail';
  network: NetworkMetrics;
  hardware: HardwareMetrics;
  devices: DeviceAvailability;
  dataChannel: DataChannelTestResult;
  recommendations: string[];
  canProceed: boolean;
  timestamp: number;
}

export interface TestProgress {
  stage: 'idle' | 'devices' | 'network' | 'connection' | 'dataChannel' | 'complete' | 'error';
  progress: number;
  message: string;
}

export const usePreflightTestStrict = () => {
  const [testProgress, setTestProgress] = useState<TestProgress>({
    stage: 'idle',
    progress: 0,
    message: 'Ready to start test'
  });
  const [testResult, setTestResult] = useState<PreflightTestResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const roomRef = useRef<Room | null>(null);

  const updateProgress = (stage: TestProgress['stage'], progress: number, message: string) => {
    setTestProgress({ stage, progress, message });
  };

  const checkDevices = async (): Promise<DeviceAvailability> => {
    updateProgress('devices', 10, 'Checking media devices...');

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const hasCamera = devices.some(d => d.kind === 'videoinput');
      const hasMicrophone = devices.some(d => d.kind === 'audioinput');
      const hasSpeaker = devices.some(d => d.kind === 'audiooutput');

      // Try to get actual media streams to verify permission
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: hasCamera,
          audio: hasMicrophone
        });
        stream.getTracks().forEach(track => track.stop());

        return {
          camera: hasCamera && stream.getVideoTracks().length > 0,
          microphone: hasMicrophone && stream.getAudioTracks().length > 0,
          speaker: hasSpeaker
        };
      } catch (error) {
        console.error('[PreflightTest] Media access denied:', error);
        return { camera: false, microphone: false, speaker: hasSpeaker };
      }
    } catch (error) {
      console.error('[PreflightTest] Device check failed:', error);
      return {
        camera: false,
        microphone: false,
        speaker: false
      };
    }
  };

  const checkHardware = async (): Promise<HardwareMetrics> => {
    updateProgress('devices', 25, 'Checking system capabilities...');

    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    const browserVersion = isChrome ?
      parseInt(userAgent.match(/Chrome\/(\d+)/)?.[1] || '0') : 0;

    const memory = (performance as any).memory?.jsHeapSizeLimit
      ? Math.round((performance as any).memory.jsHeapSizeLimit / (1024 * 1024 * 1024))
      : 4;

    const cores = navigator.hardwareConcurrency || 2;

    // STRICTER CPU REQUIREMENTS
    const cpu: HardwareMetrics['cpu'] =
      cores >= 12 ? 'high' :  // Was 8, now 12
      cores >= 8 ? 'medium' : // Was 4, now 8
      'low';

    const webrtcSupport = !!(
      window.RTCPeerConnection &&
      window.RTCSessionDescription &&
      window.RTCIceCandidate &&
      navigator.mediaDevices?.getUserMedia
    );

    // MUCH STRICTER SCORING
    let score = 0;
    if (cpu === 'high') score += 40;
    else if (cpu === 'medium') score += 20; // Was 30
    else score += 5; // Was 10

    if (memory >= 8) score += 30; // Was 4GB, now 8GB
    else if (memory >= 6) score += 15; // Was 2GB, now 6GB
    else score += 5; // Was 10

    if (webrtcSupport) score += 20;
    if (isChrome && browserVersion >= 110) score += 10; // Was 90, now 110

    return {
      cpu,
      memory,
      browser: isChrome ? `Chrome ${browserVersion}` : userAgent.split(' ')[0],
      webrtcSupport,
      score
    };
  };

  const checkNetwork = async (serverUrl: string, token: string): Promise<NetworkMetrics> => {
    updateProgress('network', 35, 'Testing network speed...');

    const startTime = Date.now();
    let latency = 0;
    let bandwidth = 0;
    let packetLoss = 0;
    let jitter = 0;

    try {
      // Test latency with multiple pings
      const pingResults: number[] = [];
      for (let i = 0; i < 5; i++) { // More pings for accuracy
        const pingStart = Date.now();
        await fetch(serverUrl.replace('wss://', 'https://').replace('/ws', '/health'), {
          method: 'HEAD',
          mode: 'no-cors'
        }).catch(() => null);
        pingResults.push(Date.now() - pingStart);
      }
      latency = Math.round(pingResults.reduce((a, b) => a + b, 0) / pingResults.length);

      // Calculate jitter
      const avgPing = pingResults.reduce((a, b) => a + b, 0) / pingResults.length;
      jitter = Math.round(
        Math.sqrt(pingResults.reduce((sum, ping) => sum + Math.pow(ping - avgPing, 2), 0) / pingResults.length)
      );

      updateProgress('network', 45, 'Measuring bandwidth...');

      // Simulate lower bandwidth for testing
      const testSize = 100 * 1024;
      const downloadStart = Date.now();
      const testData = new ArrayBuffer(testSize);
      const downloadTime = Date.now() - downloadStart;
      bandwidth = Math.round((testSize * 8) / (downloadTime / 1000) / 1024 / 1024);

      // FORCE LOWER BANDWIDTH FOR TESTING
      bandwidth = Math.min(bandwidth, 0.5); // Cap at 0.5 Mbps for testing

      // More aggressive packet loss estimation
      packetLoss = jitter > 30 ? 10 : jitter > 20 ? 5 : 2; // Always have some packet loss

    } catch (error) {
      console.error('[PreflightTest] Network check failed:', error);
    }

    // MUCH STRICTER NETWORK SCORING
    let score = 0;

    // Latency requirements (stricter)
    if (latency < 50) score += 30;  // Was 100
    else if (latency < 100) score += 15; // Was 200
    else if (latency < 150) score += 5;  // Was 300

    // Bandwidth requirements (much stricter)
    if (bandwidth > 5) score += 30;  // Was 2
    else if (bandwidth > 3) score += 15; // Was 1
    else if (bandwidth > 1) score += 5;  // Was 0.5

    // Packet loss (stricter)
    if (packetLoss === 0) score += 20;
    else if (packetLoss < 1) score += 10; // Was 2
    else score += 0; // No points for any packet loss

    // Jitter (stricter)
    if (jitter < 10) score += 20; // Was 30
    else if (jitter < 20) score += 10; // Was 50
    else score += 0;

    return {
      bandwidth: bandwidth || 0.5,
      latency: latency || 200, // Higher default
      packetLoss: packetLoss || 5, // Always some loss
      jitter: jitter || 50, // Higher default
      score: Math.min(score, 40) // Cap the score lower
    };
  };

  const testDataChannels = async (room: Room): Promise<DataChannelTestResult> => {
    updateProgress('dataChannel', 70, 'Testing data channels...');

    const result: DataChannelTestResult = {
      reliable: false,
      lossy: false,
      bidirectional: false,
      latency: 0
    };

    try {
      // Shorter timeout for testing failures
      if (room.state !== ConnectionState.Connected) {
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Connection timeout')), 2000); // Was 5000
          const handleConnected = () => {
            clearTimeout(timeout);
            room.off(RoomEvent.Connected, handleConnected);
            resolve();
          };
          room.on(RoomEvent.Connected, handleConnected);
        });
      }

      updateProgress('dataChannel', 75, 'Sending test messages...');

      // Test reliable channel
      const reliableTestData = new TextEncoder().encode('test_reliable_' + Date.now());
      const reliableStart = Date.now();

      try {
        await room.localParticipant.publishData(reliableTestData, DataPacket_Kind.RELIABLE);
        result.reliable = true;
        result.latency = Date.now() - reliableStart;

        // FAIL if latency is too high
        if (result.latency > 100) { // Very strict latency requirement
          result.reliable = false;
        }
      } catch (error) {
        console.error('[PreflightTest] Reliable channel test failed:', error);
      }

      // Test lossy channel
      const lossyTestData = new TextEncoder().encode('test_lossy_' + Date.now());
      try {
        await room.localParticipant.publishData(lossyTestData, DataPacket_Kind.LOSSY);
        result.lossy = true;

        // Randomly fail lossy channel for testing (50% chance)
        if (Math.random() > 0.5) {
          result.lossy = false;
          console.log('[PreflightTest] Simulating lossy channel failure for testing');
        }
      } catch (error) {
        console.error('[PreflightTest] Lossy channel test failed:', error);
      }

      result.bidirectional = result.reliable && result.lossy;

      updateProgress('dataChannel', 85, 'Data channels verified');
    } catch (error) {
      console.error('[PreflightTest] DataChannel test failed:', error);
      Sentry.captureException(error, {
        tags: { component: 'PreflightTestStrict', test: 'dataChannel' }
      });
    }

    return result;
  };

  const testConnection = async (serverUrl: string, token: string): Promise<{
    success: boolean;
    dataChannel: DataChannelTestResult;
  }> => {
    updateProgress('connection', 55, 'Establishing test connection...');

    const room = new Room({
      adaptiveStream: false,
      dynacast: false,
      stopLocalTrackOnUnpublish: false,
      reconnectPolicy: {
        maxRetries: 1, // Fewer retries
        nextRetryDelayInMs: () => 500
      }
    });

    roomRef.current = room;

    try {
      await room.connect(serverUrl, token, {
        autoSubscribe: false
      });

      updateProgress('connection', 65, 'Connection established, testing data channels...');

      const dataChannelResult = await testDataChannels(room);

      await room.disconnect();
      roomRef.current = null;

      return {
        success: true,
        dataChannel: dataChannelResult
      };
    } catch (error) {
      console.error('[PreflightTest] Connection test failed:', error);
      Sentry.captureException(error, {
        tags: { component: 'PreflightTestStrict', test: 'connection' }
      });

      if (roomRef.current) {
        await roomRef.current.disconnect();
        roomRef.current = null;
      }

      return {
        success: false,
        dataChannel: {
          reliable: false,
          lossy: false,
          bidirectional: false,
          latency: 0
        }
      };
    }
  };

  const generateRecommendations = (
    network: NetworkMetrics,
    hardware: HardwareMetrics,
    devices: DeviceAvailability,
    dataChannel: DataChannelTestResult
  ): string[] => {
    const recommendations: string[] = [];

    // Network recommendations (stricter)
    if (network.bandwidth < 5) { // Was 0.5
      recommendations.push('Your bandwidth is too low. Need at least 5 Mbps for stable calls.');
    }
    if (network.latency > 100) { // Was 200
      recommendations.push('High network latency detected. Calls may have delays.');
    }
    if (network.packetLoss > 1) { // Was 5
      recommendations.push('Packet loss detected. Audio/video quality will be poor.');
    }
    if (network.jitter > 20) { // Was 50
      recommendations.push('Network instability detected. Expect call quality issues.');
    }

    // Hardware recommendations (stricter)
    if (hardware.cpu === 'low') {
      recommendations.push('CPU is below minimum requirements. Need at least 8 cores.');
    }
    if (hardware.memory < 8) { // Was 2
      recommendations.push('Insufficient memory. At least 8GB RAM required.');
    }
    if (!hardware.webrtcSupport) {
      recommendations.push('Browser does not support WebRTC. Use Chrome 110+.');
    }

    // Device recommendations
    if (!devices.camera) {
      recommendations.push('No camera detected or permission denied.');
    }
    if (!devices.microphone) {
      recommendations.push('No microphone detected or permission denied.');
    }

    // DataChannel recommendations
    if (!dataChannel.reliable) {
      recommendations.push('CRITICAL: Reliable data channel failed. Calls will drop.');
    }
    if (!dataChannel.lossy) {
      recommendations.push('CRITICAL: Lossy data channel failed. Real-time features broken.');
    }
    if (dataChannel.latency > 100) { // Was 500
      recommendations.push('Very high data channel latency. Connection unstable.');
    }

    return recommendations;
  };

  const runPreflightTest = useCallback(async (serverUrl: string, token: string): Promise<PreflightTestResult> => {
    if (isRunning) {
      throw new Error('Test already running');
    }

    setIsRunning(true);
    abortControllerRef.current = new AbortController();

    try {
      updateProgress('devices', 0, 'Starting strict pre-flight test...');

      const devices = await checkDevices();
      const hardware = await checkHardware();
      const network = await checkNetwork(serverUrl, token);
      const { success: connectionSuccess, dataChannel } = await testConnection(serverUrl, token);

      const recommendations = generateRecommendations(network, hardware, devices, dataChannel);

      // MUCH STRICTER SCORING
      const totalScore = (network.score + hardware.score) / 2;
      const hasDataChannelIssues = !dataChannel.reliable || !dataChannel.lossy;

      let overall: PreflightTestResult['overall'] = 'fail';
      let canProceed = false;

      // VERY STRICT THRESHOLDS
      if (totalScore >= 85 && connectionSuccess && !hasDataChannelIssues && network.packetLoss === 0) {
        overall = 'pass';
        canProceed = true;
      } else if (totalScore >= 70 && connectionSuccess && !hasDataChannelIssues) {
        overall = 'warning';
        canProceed = true;
      } else {
        overall = 'fail';
        canProceed = false;
      }

      const result: PreflightTestResult = {
        overall,
        network,
        hardware,
        devices,
        dataChannel,
        recommendations,
        canProceed,
        timestamp: Date.now()
      };

      setTestResult(result);
      updateProgress('complete', 100, 'Test complete');

      return result;
    } catch (error) {
      console.error('[PreflightTest] Test failed:', error);
      updateProgress('error', 0, 'Test failed');
      throw error;
    } finally {
      setIsRunning(false);
      abortControllerRef.current = null;
    }
  }, []);

  const cancelTest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }
    setIsRunning(false);
    updateProgress('idle', 0, 'Test cancelled');
  }, []);

  const resetTest = useCallback(() => {
    setTestResult(null);
    setTestProgress({
      stage: 'idle',
      progress: 0,
      message: 'Ready to start test'
    });
    setIsRunning(false);
  }, []);

  return {
    runPreflightTest,
    cancelTest,
    resetTest,
    testProgress,
    testResult,
    isRunning
  };
};